/**
 * kunstkaesten-stills-cms – Static PNG exports, data pre-filled from CMS
 *
 * Same visual output as kunstkaesten-stills, but fetches exhibition data from
 * the Nuxt server route http://localhost:3333/api/some/aktuell and pre-populates
 * artistName, dateSpan, exhibitionTheme and the aktuell panel image automatically.
 *
 * Any input field that is manually filled in overrides the CMS value.
 * mechanic.done() is called only after the fetch resolves so the still frame
 * always reflects live CMS data.
 */

import React, { useEffect, useState, useMemo, useRef } from "react";
import { animate } from "animejs";
import { COMPONENT_ORDER } from "../kunstkaesten/useCrossRotation";
import "../kunstkaesten/styles.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const CMS_BASE = "https://kunstkaesten.hakanya.kim";
const CMS_URL  = `${CMS_BASE}/api/some/aktuell`;

const FLIPPED_PANELS = new Set(["ausblick", "info"]);
const BASE_ROTATIONS = { aktuell: 0, ausblick: 90, info: 0, archiv: 90 };
const POSITION_INDEX = { aktuell: 0, ausblick: 1, info: 2, archiv: 3 };
const ACTIVE_STEPS = ["1", "2", "3", "4"];
const STEP_KEY = { "1": "aktuell", "2": "ausblick", "3": "info", "4": "archiv" };

/** Format ISO "YYYY-MM-DD" → "DD.MM.YYYY" */
function fmtDateFull(iso) {
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}.${mm}.${yyyy}`;
}

/** Format ISO date or timestamp → "3. März 2026" (German long form) */
function fmtGermanDate(isoOrDate) {
  if (!isoOrDate) return "";
  const dateStr = String(isoOrDate).split("T")[0];
  const [yyyy, mm, dd] = dateStr.split("-");
  const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  return new Intl.DateTimeFormat("de-CH", { day: "numeric", month: "long", year: "numeric" }).format(date);
}

/** Format ISO "YYYY-MM-DD" → "DD.MM." */
function fmtDay(iso) {
  if (!iso) return "";
  const [, mm, dd] = iso.split("-");
  return `${dd}.${mm}.`;
}

/** Build a date-span string like "01.02. – 31.05.26" from two ISO dates. */
function buildDateSpan(start, end) {
  if (!start && !end) return "";
  const s = fmtDay(start);
  const e = end ? `${fmtDay(end)}${end.slice(2, 4)}` : "";
  return `${s} – ${e}`;
}

/** Absolute rotation for each panel after `steps` × 90° have been applied. */
function rotationsAtStep(steps) {
  return Object.fromEntries(
    COMPONENT_ORDER.map((k) => [k, BASE_ROTATIONS[k] + steps * 90]),
  );
}

// ─── Handler ──────────────────────────────────────────────────────────────────

export const handler = ({ inputs, mechanic }) => {
  // ── Fetch CMS data + convert images to blob URLs once on mount ─────────────
  const [cmsData, setCmsData]         = useState(null);
  const [cmsFetched, setCmsFetched]   = useState(false);
  const [cmsBlobUrls, setCmsBlobUrls] = useState({});

  useEffect(() => {
    let active = true;

    async function load() {
      let data = null;
      try {
        const res = await fetch(CMS_URL);
        data = await res.json();
      } catch (_) {}

      if (!active) return;
      setCmsData(data);

      // Fetch images as blobs so Mechanic's canvas can draw them without taint
      const blobs = {};
      const imgPaths = [
        ['image',         data?.image],
        ['imageKuenstler', data?.imageKuenstler ?? data?.image],
      ];
      await Promise.all(
        imgPaths.map(async ([key, path]) => {
          if (!path) return;
          try {
            const r = await fetch(`${CMS_BASE}${path}`);
            if (r.ok) blobs[key] = URL.createObjectURL(await r.blob());
          } catch (_) {}
        })
      );

      if (active) {
        setCmsBlobUrls(blobs);
        setCmsFetched(true);
      }
    }

    load();
    return () => { active = false; };
  }, []);

  // ── Destructure inputs ────────────────────────────────────────────────────
  const {
    width,
    height,
    backgroundColor,
    textColor,
    inactiveBlur,
    inactiveOpacity,
    activeStep,
    label1,
    label2,
    label3,
    label4,
    exhibitionTitle,
    artistName,
    dateSpan,
    vernissageDate,
    vernissageTime,
    vernissageAddress,
    exhibitionTheme,
    artistDetails,
    imageAktuell,
    imageAusblick,
    imageInfo,
    imageArchiv,
  } = inputs;

  // ── Merge CMS data with manual input overrides (inputs win when non-empty) ─
  const resolvedArtistName        = (artistName        && artistName.trim())        ? artistName        : (cmsData?.artist           ?? "");
  const resolvedExhibTitle        = (exhibitionTitle   && exhibitionTitle.trim())   ? exhibitionTitle   : (cmsData?.werktitel        ?? "");
  const resolvedDateSpan          = (dateSpan          && dateSpan.trim())          ? dateSpan          : buildDateSpan(cmsData?.startDate, cmsData?.endDate);
  const resolvedVernissageDate    = (vernissageDate    && vernissageDate.trim())    ? vernissageDate    : fmtGermanDate(cmsData?.vernissage?.datum);
  const resolvedVernissageTime    = (vernissageTime    && vernissageTime.trim())    ? vernissageTime    : (cmsData?.vernissage?.uhrzeit   ?? "");
  const resolvedVernissageAddress = (vernissageAddress && vernissageAddress.trim()) ? vernissageAddress : (cmsData?.vernissage?.ort       ?? "");
  const resolvedExhibTheme        = (exhibitionTheme   && exhibitionTheme.trim())   ? exhibitionTheme   : (cmsData?.kuenstlerBio      ?? "");
  const resolvedArtistDetails     = (artistDetails     && artistDetails.trim())     ? artistDetails     : (cmsData?.description       ?? "");

  const activePanelKey = STEP_KEY[activeStep];

  // CMS images are pre-fetched as same-origin blob URLs to avoid canvas taint on export
  const cmsImageSrc       = cmsBlobUrls.image         ?? null;
  const cmsArtistImageSrc = cmsBlobUrls.imageKuenstler ?? cmsImageSrc;

  // Build panel image URLs (recomputed when any image input or CMS src changes)
  const panelImageUrls = useMemo(() => ({
    aktuell:  imageAktuell  ? URL.createObjectURL(imageAktuell)  : null,
    ausblick: imageAusblick ? URL.createObjectURL(imageAusblick) : null,
    info:     imageInfo     ? URL.createObjectURL(imageInfo)     : cmsArtistImageSrc,
    archiv:   imageArchiv   ? URL.createObjectURL(imageArchiv)   : null,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [imageAktuell, imageAusblick, imageInfo, imageArchiv, cmsArtistImageSrc]);

  const panelLabels = {
    aktuell:  label1,
    ausblick: label2,
    info:     label3,
    archiv:   label4,
  };

  // ── Refs ───────────────────────────────────────────────────────────────────
  const panelRefs   = useRef({});
  const setPanelRef = (key) => (el) => { panelRefs.current[key] = el; };

  const panelImgRefs   = useRef({});
  const setPanelImgRef = (key) => (el) => { panelImgRefs.current[key] = el; };

  const imgSlotRefs   = useRef({});
  const setImgSlotRef = (key) => (el) => { imgSlotRefs.current[key] = el; };

  // ── Apply end-state styles then call mechanic.done() ──────────────────────
  // Wait until fetch resolves so CMS content is rendered before capturing.
  useEffect(() => {
    if (!cmsFetched) return;

    const BLUR_INACTIVE    = `blur(${inactiveBlur}px)`;
    const OPACITY_INACTIVE = inactiveOpacity;
    const rots = rotationsAtStep(POSITION_INDEX[activePanelKey]);

    // Disable CSS transitions so anime.js duration:0 takes effect immediately.
    COMPONENT_ORDER.forEach((key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      el.style.transition = "none";
      const stepname = el.querySelector(".cross-stepname");
      const body     = el.querySelector(".cross-body");
      if (stepname) stepname.style.transition = "none";
      if (body)     body.style.transition     = "none";
    });

    COMPONENT_ORDER.forEach((key) => {
      const el = panelRefs.current[key];
      if (!el) return;
      const isFlipped = FLIPPED_PANELS.has(key);
      const isActive  = key === activePanelKey;

      if (panelImageUrls[key]) {
        animate(el, {
          opacity: 1,
          filter:  "blur(0px)",
          rotate:  rots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
        const stepname = el.querySelector(".cross-stepname");
        const body     = el.querySelector(".cross-body");
        if (stepname) animate(stepname, { opacity: OPACITY_INACTIVE, filter: BLUR_INACTIVE, duration: 0 });
        if (body)     animate(body,     { opacity: OPACITY_INACTIVE, filter: BLUR_INACTIVE, duration: 0 });
      } else {
        animate(el, {
          opacity: isActive ? 1 : OPACITY_INACTIVE,
          filter:  isActive ? "blur(0px)" : BLUR_INACTIVE,
          rotate:  rots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
      }

      if (imgSlotRefs.current[key]) {
        animate(imgSlotRefs.current[key], {
          rotate: rots[key],
          ...(isFlipped ? { scaleX: -1, scaleY: -1 } : {}),
          duration: 0,
        });
      }

      if (panelImageUrls[key] && panelImgRefs.current[key]) {
        animate(panelImgRefs.current[key], { opacity: isActive ? 1 : 0, duration: 0 });
      }
    });

    requestAnimationFrame(() => mechanic.done());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsFetched]);

  // ── Panel body content ─────────────────────────────────────────────────────
  const panelContent = {
    aktuell: (
      <>
        <h1 className="cross-line">{resolvedArtistName}</h1>
        <h1 className="cross-line" style={{ fontStyle: "italic" }}>
          {resolvedExhibTitle}
        </h1>
        <h1 className="cross-line">{resolvedDateSpan}</h1>
      </>
    ),
    ausblick: (
      <>
        <h1 className="cross-line">{resolvedVernissageDate}</h1>
        <h1 className="cross-line">{resolvedVernissageAddress}</h1>
        <h1 className="cross-line">{resolvedVernissageTime}</h1>
      </>
    ),
    info: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {resolvedExhibTheme}
        </p>
      </>
    ),
    archiv: (
      <>
        <p className="cross-line-wrap" style={{ width: "170%" }}>
          {resolvedArtistDetails}
        </p>
      </>
    ),
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="cross-root"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor,
        color: textColor,
      }}
    >
      {/* Image slots — rendered below panels so text layers sit on top */}
      {COMPONENT_ORDER.map((key) => {
        const imgUrl = panelImageUrls[key];
        if (!imgUrl) return null;
        return (
          <div
            key={`img-${key}`}
            ref={setImgSlotRef(key)}
            className={`cross-image-slot cross-image-slot-${key}`}
          >
            <img
              ref={setPanelImgRef(key)}
              src={imgUrl}
              alt=""
              className="cross-panel-image"
              style={{ opacity: 0 }}
            />
          </div>
        );
      })}
      {/* Panel containers */}
      {COMPONENT_ORDER.map((key) => (
        <div key={key} className="cross-container">
          <div ref={setPanelRef(key)} className={`cross-panel cross-${key}`}>
            <h3 className="cross-stepname">{panelLabels[key]}</h3>
            <div className="cross-body">{panelContent[key]}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Inputs ───────────────────────────────────────────────────────────────────

export const inputs = {
  // ── Canvas ────────────────────────────────────────────────────────────────
  width: {
    type: "number",
    default: 1080,
    label: "Width (px)",
  },
  height: {
    type: "number",
    default: 1350,
    label: "Height (px)",
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#ffffff",
    label: "Background",
  },
  textColor: {
    type: "color",
    model: "hex",
    default: "#0a1ef5",
    label: "Text Color",
  },
  inactiveBlur: {
    type: "number",
    default: 9,
    min: 0,
    max: 40,
    slider: true,
    label: "Inactive Blur (px)",
  },
  inactiveOpacity: {
    type: "number",
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    slider: true,
    label: "Inactive Opacity",
  },

  // ── Step selector ──────────────────────────────────────────────────────────
  activeStep: {
    type: "text",
    default: "1",
    options: ACTIVE_STEPS,
    label: "Active Step",
  },

  // ── Step name labels ───────────────────────────────────────────────────────
  label1: {
    type: "text",
    default: "Neue Ausstellung",
    label: "Label – 1",
  },
  label2: {
    type: "text",
    default: "Vernissage",
    label: "Label – 2",
  },
  label3: {
    type: "text",
    default: "Zum Künstler",
    label: "Label – 3",
  },
  label4: {
    type: "text",
    default: "Zur Ausstellung",
    label: "Label – 4",
  },

  // ── Step 1 – Aktuell (auto-filled from CMS; override below) ───────────────
  artistName: {
    type: "text",
    default: "",
    label: "Artist Name (override CMS)",
  },
  exhibitionTitle: {
    type: "text",
    default: "",
    label: "Werktitel (override CMS)",
  },
  dateSpan: {
    type: "text",
    default: "",
    label: "Date Span (override CMS)",
  },

  // ── Step 2 – Ausblick (Vernissage; auto-filled from CMS) ──────────────────
  vernissageDate: {
    type: "text",
    default: "",
    label: "Vernissage Date (override CMS)",
  },
  vernissageAddress: {
    type: "text",
    default: "",
    label: "Vernissage Address (override CMS)",
  },
  vernissageTime: {
    type: "text",
    default: "",
    label: "Vernissage Time (override CMS)",
  },

  // ── Step 3 – Info (auto-filled from CMS artist bio) ───────────────────────
  exhibitionTheme: {
    type: "text",
    default: "",
    label: "Künstler:in Bio (override CMS)",
  },

  // ── Step 4 – Archiv (auto-filled from CMS description) ────────────────────
  artistDetails: {
    type: "text",
    default: "",
    label: "Kurzbeschreibung Werk (override CMS)",
  },

  // ── Panel images (optional; aktuell falls back to CMS image) ──────────────
  imageAktuell: {
    type: "image",
    label: "Image – 1 (optional, sonst nur Text)",
  },
  imageAusblick: {
    type: "image",
    label: "Image – 2",
  },
  imageInfo: {
    type: "image",
    label: "Image – 3 (override CMS artist portrait)",
  },
  imageArchiv: {
    type: "image",
    label: "Image – 4",
  },
};

// ─── Presets ──────────────────────────────────────────────────────────────────

export const presets = {
  portrait: { width: 1080, height: 1350 },
  square:   { width: 1080, height: 1080 },
};

// ─── Settings ─────────────────────────────────────────────────────────────────

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Kunstkaesten Stills CMS",
  animated: false,
  hideScaleToFit: true,
  initialScaleToFit: false,
  hideAutoRefresh: true,
};
